/**
 * Buffer de erros PRÉ-SDK.
 *
 * O SDK do Sentry entra por chunk lazy, depois do boot (é o que mantém o
 * caminho crítico intacto e os gates de bundle verdes). A janela entre o
 * primeiro byte de JS e a chegada do SDK é justamente onde moram os erros mais
 * caros — os de boot. Sem buffer, "lazy" significaria "cego no boot".
 *
 * Então: listeners baratos (~1 KB, sem dependência nenhuma) são instalados no
 * `main.ts` antes do bootstrap, enfileiram o que aparecer, e o SDK faz o replay
 * da fila assim que inicializa. Se o SDK nunca chegar (gate reprovou, rede
 * falhou), a fila é descartada — ela nunca é o dono do dado, só a ponte.
 *
 * Invariantes: nunca lança, nunca cresce sem teto, nunca duplica listener.
 */

export type OrigemErro = 'window' | 'promise' | 'angular';

export interface ErroBufferizado {
  error: any;
  origem: OrigemErro;
  /** ms desde a navegação (performance.now), para o SDK datar o replay. */
  emMs: number;
}

/**
 * Teto da fila. 20 basta: o que interessa é o PRIMEIRO erro do boot (a causa)
 * — do 21º em diante já é cascata, e segurar mais só aumenta a memória
 * retida numa página que pode estar quebrando.
 */
export const MAX_BUFFER = 20;

let fila: ErroBufferizado[] = [];
let instalado = false;
let descartados = 0;
let aoReceber: (() => void) | null = null;

let onError: ((e: ErrorEvent) => void) | null = null;
let onRejection: ((e: PromiseRejectionEvent) => void) | null = null;

function agora(): number {
  try {
    return (typeof performance !== 'undefined' && performance.now) ? Math.round(performance.now()) : 0;
  } catch (e) {
    return 0;
  }
}

/**
 * Enfileira um erro. Devolve `false` quando a fila está cheia (o chamador não
 * precisa fazer nada com isso — é só para o teste poder afirmar o teto).
 */
export function enfileirar(error: any, origem: OrigemErro): boolean {
  try {
    if (fila.length >= MAX_BUFFER) {
      descartados++;
      return false;
    }
    fila.push({ error, origem, emMs: agora() });
    // Avisa quem espera (o SentryService antecipa a carga do SDK quando um erro
    // aparece — não faz sentido esperar o idle de uma página que já quebrou).
    if (aoReceber) {
      try { aoReceber(); } catch (e) { /* callback nunca derruba o buffer */ }
    }
    return true;
  } catch (e) {
    return false;
  }
}

/** Registra o observador de chegada (um só; o último registrado vence). */
export function observarChegada(cb: (() => void) | null): void {
  aoReceber = cb;
}

/**
 * Instala os listeners globais. Idempotente.
 *
 * `addEventListener`, NUNCA `window.onerror =`: atribuição é canal ÚNICO — o
 * último a escrever apaga quem estava lá, sem aviso. Foi exatamente o que o
 * bloco do in-app browser do Instagram fazia no `index.html` até 28/08/2026
 * (removido: despejava cada erro num `alert()` na cara de quem estava no
 * checkout). Ele já não existe, mas a regra continua: qualquer script de
 * terceiro pode fazer o mesmo, e com listener os canais convivem.
 */
export function instalarCapturaPrecoce(): void {
  try {
    if (instalado || typeof window === 'undefined') return;
    onError = (ev: ErrorEvent) => {
      enfileirar(ev && (ev as any).error ? (ev as any).error : (ev && ev.message) || 'Error', 'window');
    };
    onRejection = (ev: PromiseRejectionEvent) => {
      enfileirar(ev && (ev as any).reason, 'promise');
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection as any);
    instalado = true;
  } catch (e) {
    // Sem captura precoce o app segue igual — este módulo é aditivo.
  }
}

/**
 * Remove os listeners. Chamado quando o SDK inicializa: dali em diante quem
 * escuta `error`/`unhandledrejection` é o `globalHandlersIntegration` do
 * próprio Sentry, e manter os nossos duplicaria todo erro global.
 */
export function desinstalarCapturaPrecoce(): void {
  try {
    if (!instalado || typeof window === 'undefined') return;
    if (onError) window.removeEventListener('error', onError);
    if (onRejection) window.removeEventListener('unhandledrejection', onRejection as any);
  } catch (e) {
    // ignorado
  }
  onError = null;
  onRejection = null;
  instalado = false;
}

/** Esvazia e devolve a fila (drenar é destrutivo: nada é reprocessado 2×). */
export function drenar(): ErroBufferizado[] {
  const out = fila;
  fila = [];
  return out;
}

/**
 * Descarta a fila sem entregar — gate reprovou, ninguém vai consumir.
 *
 * Zera `descartados` junto, e isso é deliberado: a contagem só existe para
 * virar tag no primeiro evento enviado, e aqui está decidido que NENHUM evento
 * será enviado. Preservá-la deixaria estado global sujo para o próximo uso (e
 * entre specs) em troca de um número que ninguém leria.
 */
export function descartar(): void {
  fila = [];
  descartados = 0;
}

export function tamanhoBuffer(): number {
  return fila.length;
}

/** Quantos erros a fila recusou por estar cheia (vira tag no primeiro evento). */
export function totalDescartados(): number {
  return descartados;
}

export function estaInstalado(): boolean {
  return instalado;
}
